import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Hls from 'hls.js';

const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: false,
  backBufferLength: 60,
  maxBufferLength: 60,
  maxMaxBufferLength: 120,
  fragLoadingMaxRetry: 6,
  manifestLoadingMaxRetry: 6,
  levelLoadingMaxRetry: 6,
  fragLoadingRetryDelay: 1000,
  manifestLoadingRetryDelay: 1000,
  levelLoadingRetryDelay: 1000,
};

function heightLabel(h) {
  if (!h || h <= 0) return 'Auto';
  return `${h}p`;
}

export default function HlsPlayer({
  src,
  className,
  autoPlay = false,
  controls = true,
  playsInline = true,
  backendUrl,
  onTimeUpdate,
  onPause,
  onEnded,
  onReady,
  onError,
  onRefreshSource, // optional: called when 401/403 signed URL expiry detected
  videoRef // optional: callers can pass a ref to access the video element
}) {
  const internalRef = useRef(null);
  const hlsRef = useRef(null);
  const [qualities, setQualities] = useState([]);
  const [selected, setSelected] = useState('auto');
  const refreshAttemptsRef = useRef(0);
  const lastRefreshTimeRef = useRef(0);

  // stable helpers and refs to avoid re-creating Hls instance on every render
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);
  const onRefreshSourceRef = useRef(onRefreshSource);
  const autoPlayRef = useRef(autoPlay);
  const externalVideoRef = useRef(videoRef);

  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  useEffect(() => { onRefreshSourceRef.current = onRefreshSource; }, [onRefreshSource]);
  useEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);
  useEffect(() => { externalVideoRef.current = videoRef; }, [videoRef]);

  const resolveSrc = useCallback((s) => {
    if (!s) return s;
    if (s.startsWith('http')) return s;
    if (backendUrl) return `${backendUrl}${s}`;
    return s;
  }, [backendUrl]);

  useEffect(() => {
    const videoEl = (externalVideoRef.current && externalVideoRef.current.current) || internalRef.current;
    if (!videoEl || !src) return undefined;

    const resolved = resolveSrc(src);

    // Clean up any existing hls
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch (e) { /* ignore */ }
      hlsRef.current = null;
    }

    let mounted = true;

    const setupNative = () => {
      videoEl.src = resolved;
      if (onReadyRef.current) onReadyRef.current({ native: true });
      return null;
    };

    if (videoEl.canPlayType && videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS (Safari/iOS)
      setupNative();
      return () => {
        try { videoEl.removeAttribute('src'); videoEl.load(); } catch (e) {}
      };
    }

    if (!Hls.isSupported()) {
      // Nothing we can do
      if (onErrorRef.current) onErrorRef.current({ reason: 'HLS_NOT_SUPPORTED' });
      return undefined;
    }

    const hls = new Hls(HLS_CONFIG);
    hlsRef.current = hls;

    const onManifest = () => {
      if (!mounted) return;
      const levels = (hls.levels || []).slice();
      if (!levels || levels.length === 0) {
        setQualities([]);
        return;
      }

      // Build unique heights picking highest bitrate per height
      const map = new Map();
      levels.forEach((lvl, idx) => {
        const h = lvl.height || 0;
        const current = map.get(h);
        const bitrate = lvl.bitrate || lvl?.bitrate || 0;
        if (!current || bitrate > (current.bitrate || 0)) {
          map.set(h, { idx, bitrate });
        }
      });

      const entries = Array.from(map.entries())
        .map(([height, info]) => ({ height, idx: info.idx }))
        .sort((a, b) => b.height - a.height);

      const opts = entries.map(e => ({ label: heightLabel(e.height), value: String(e.height), index: e.idx }));
      if (opts.length >= 2) {
        setQualities(opts);
      } else {
        setQualities([]);
      }

      if (onReadyRef.current) onReadyRef.current({ native: false, levels: levels.length });
    };

    const onLevelLoaded = (_event, _data) => {
      // update qualities when levels load
      onManifest();
    };

    const onErrorEvent = (_event, data) => {
      if (!data) return;
      if (!data.fatal) return;

      // Check for 401/403 signed URL expiry (manifest or fragment load failure)
      const status = data.response?.status || data.status;
      const is403or401 = status === 403 || status === 401;
      const isManifestOrFrag = data.type === Hls.ErrorTypes.NETWORK_ERROR &&
        (data.context?.type === 'manifest' || data.context?.type === 'level' || data.context?.type === 'frag');

      if (is403or401 && isManifestOrFrag && onRefreshSourceRef.current) {
        // Attempt to refresh the source URL (signed URL may have expired)
        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshTimeRef.current;

        // Debounce: only retry refresh once per 5 seconds
        if (timeSinceLastRefresh < 5000) return;

        // Limit refresh attempts to 3 per session to avoid infinite retry
        if (refreshAttemptsRef.current >= 3) {
          // Give up, call error handler
          try { hls.destroy(); } catch (e) {}
          hlsRef.current = null;
          if (onErrorRef.current) onErrorRef.current(data);
          return;
        }

        refreshAttemptsRef.current += 1;
        lastRefreshTimeRef.current = now;

        // Save current playback position
        const videoEl = (externalVideoRef.current && externalVideoRef.current.current) || internalRef.current;
        const currentTime = videoEl?.currentTime || 0;
        const wasPlaying = videoEl && !videoEl.paused;

        // Request fresh stream URL
        onRefreshSourceRef.current()
          .then((newUrl) => {
            if (!mounted) return;
            // Reload HLS with new signed URL
            const resolved = resolveSrc(newUrl);
            try {
              if (hlsRef.current) {
                hlsRef.current.loadSource(resolved);
                // Restore playback position
                if (videoEl) {
                  videoEl.currentTime = currentTime;
                  if (wasPlaying) {
                    const p = videoEl.play();
                    if (p && p.catch) p.catch(() => {});
                  }
                }
              }
            } catch (e) {
              console.error('Failed to reload HLS source', e);
            }
          })
          .catch((err) => {
            console.error('Failed to refresh stream URL', err);
            try { hls.destroy(); } catch (e) {}
            hlsRef.current = null;
            if (onErrorRef.current) onErrorRef.current(data);
          });
        return;
      }

      // Standard recovery for other network/media errors
      try {
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          // try restarting load
          hls.startLoad();
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }
      } catch (e) {
        // fallthrough to destroy
      }
      // non-recoverable
      try { hls.destroy(); } catch (e) {}
      hlsRef.current = null;
      if (onErrorRef.current) onErrorRef.current(data);
    };

    hls.on(Hls.Events.MANIFEST_PARSED, onManifest);
    hls.on(Hls.Events.LEVEL_LOADED, onLevelLoaded);
    hls.on(Hls.Events.ERROR, onErrorEvent);

    hls.loadSource(resolved);
    hls.attachMedia(videoEl);

    // autoplay handling
    if (autoPlayRef.current) {
      const p = videoEl.play();
      if (p && p.catch) p.catch(() => {});
    }

    mounted = true;

    return () => {
      mounted = false;
      try {
        if (hlsRef.current) {
          hlsRef.current.off(Hls.Events.MANIFEST_PARSED, onManifest);
          hlsRef.current.off(Hls.Events.LEVEL_LOADED, onLevelLoaded);
          hlsRef.current.off(Hls.Events.ERROR, onErrorEvent);
          hlsRef.current.destroy();
        }
      } catch (e) {}
      hlsRef.current = null;
      try { videoEl.removeAttribute('src'); videoEl.load(); } catch (e) {}
    };
  }, [src, resolveSrc]);

  const handleQualityChange = (e) => {
    const v = e.target.value;
    setSelected(v);
    const hls = hlsRef.current;
    if (!hls) return;
    if (v === 'auto') {
      hls.currentLevel = -1;
      return;
    }
    const height = Number(v);
    // find matching level index (prefer highest bitrate for height)
    const levels = hls.levels || [];
    let chosen = -1;
    let bestBitrate = -1;
    levels.forEach((lvl, idx) => {
      const h = lvl.height || 0;
      if (h === height) {
        const b = lvl.bitrate || lvl?.bitrate || 0;
        if (b > bestBitrate) { bestBitrate = b; chosen = idx; }
      }
    });
    if (chosen >= 0) hls.currentLevel = chosen;
  };

  return (
    <div className="relative w-full h-full">
      <video
        ref={(el) => { internalRef.current = el; if (videoRef) videoRef.current = el; }}
        className={className}
        controls={controls}
        autoPlay={autoPlay}
        playsInline={playsInline}
        onTimeUpdate={onTimeUpdate}
        onPause={onPause}
        onEnded={onEnded}
      />

      {qualities.length >= 2 && (
        <div className="absolute top-2 right-2 z-20">
          <select
            value={selected}
            onChange={handleQualityChange}
            className="bg-black/70 text-white text-xs px-2 py-1 rounded-sm"
            aria-label="Quality selector"
          >
            <option value="auto">Auto</option>
            {qualities.map(q => (
              <option key={q.value} value={q.value}>{q.label}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

HlsPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  className: PropTypes.string,
  autoPlay: PropTypes.bool,
  controls: PropTypes.bool,
  playsInline: PropTypes.bool,
  backendUrl: PropTypes.string,
  onTimeUpdate: PropTypes.func,
  onPause: PropTypes.func,
  onEnded: PropTypes.func,
  onReady: PropTypes.func,
  onError: PropTypes.func,
  onRefreshSource: PropTypes.func,
  videoRef: PropTypes.object,
};
